import type { GetStaticProps } from "next"
import { type Metadata, PageMeta } from "@/components/common/Seo/PageMeta"
import { LoadingIndicatorBox } from "@/components/resources/components/LoadingIndicatorBox"
import {
  type BeehiivPostWithContent,
  getPostBySlug,
  getPosts,
} from "@/components/resources/data/beehiiv/client"
import { SiteLayout } from "@/components/resources/layouts/site"
import { BeehiivArticlePage } from "@/components/resources/templates/BeehiivArticlePage"
import { shouldSkipStaticBuild } from "@/components/resources/utils/build"
import { BEEHIIV_PUBLICATION_ID } from "@/constants/env"

type PageProps = {
  post: BeehiivPostWithContent
  meta: Metadata
}

const SinglePost = ({ post, meta }: PageProps) => {
  if (!post) {
    return <LoadingIndicatorBox />
  }

  return (
    <>
      <PageMeta meta={meta} />
      <BeehiivArticlePage post={post} />
    </>
  )
}

SinglePost.getLayout = SiteLayout

export default SinglePost

export async function getStaticPaths() {
  if (shouldSkipStaticBuild()) {
    return {
      paths: [],
      fallback: true,
    }
  }

  try {
    const posts = await getPosts(BEEHIIV_PUBLICATION_ID!)

    return {
      paths: posts.data.map(({ id }) => ({
        params: { category: "newsletter", slug: id },
      })),
      fallback: true, // Allow publishing new posts without build
    }
  } catch (error) {
    console.error(
      "Failed to fetch newsletter posts for static paths, falling back to empty paths:",
      error,
    )
    return {
      paths: [],
      fallback: true,
    }
  }
}

export const getStaticProps: GetStaticProps<PageProps, { slug?: string }> = async ({ params }) => {
  const { slug } = params ?? {}

  if (!slug) {
    throw new Error("Slug cannot be empty, check getStaticPaths")
  }

  const post = await getPostBySlug(BEEHIIV_PUBLICATION_ID!, slug)

  if (!post.data) {
    return {
      notFound: true,
      props: {},
    }
  }

  return {
    props: {
      post: post.data,
      meta: {
        title: post.data.title,
        description: post.data.meta_default_description,
        canonical: null,
        image: post.data.thumbnail_url ?? null,
        type: "article",
        url: null,
        noIndex: false,
      },
    },
    revalidate: 180, // TODO: temporary to allow content editing without an hour wait for build
  }
}
